import React, { useCallback, useEffect, useRef, useState } from "react"
import {
  ActivityIndicator,
  AppState,
  AppStateStatus,
  Keyboard,
  Platform,
  StyleSheet,
  Text,
  TextInput,
  TextStyle,
  TouchableOpacity,
  View,
  ViewStyle,
} from "react-native"

import IonIcons from "@expo/vector-icons/Ionicons"
import MaterialIcons from "@expo/vector-icons/MaterialIcons"
import { observer } from "mobx-react-lite"
import { KeyboardEvents } from "react-native-keyboard-controller"
import Animated, {
  interpolate,
  useAnimatedStyle,
  useSharedValue,
  withSpring,
} from "react-native-reanimated"

import type { ControlsConfig, NavigationControlsConfig } from "src/components/SidekickView/types"
import { TalkIcon } from "src/components/icons/TalkIcon"
import { ThinkingDots } from "src/components/SidekickChat/ThinkingDots"
import { WaveformAnimation } from "src/components/SidekickChat/WaveformAnimation"
import { useChat } from "src/context/ChatContext"
import { useSidekickInteraction } from "src/context/SidekickInteractionContext"
import { useTyping } from "src/context/TypingContext"
import { useAppTheme } from "src/hooks/useAppTheme"
import { useMicrophoneStream } from "src/hooks/useMicrophoneStream"
import { ThemedStyle } from "src/theme"

/**
 * ⚠️ FINDING — `TextInput.onContentSizeChange` is broken on iOS (and unreliable
 * on web), which is why this component measures text height with a hidden mirror
 * `<Text>` instead of trusting the input's own content-size callback.
 *
 * iOS (React Native New Architecture / Fabric):
 *   - Since RN 0.79 (we are on 0.79.5), `onContentSizeChange` for a multiline
 *     TextInput fires ONLY once, on mount. It never fires again as the text
 *     grows or shrinks, so the input cannot auto-grow and we get no height
 *     signal. Confirmed still broken on 0.80 / 0.81; no upstream fix yet.
 *   - Tracking issue: https://github.com/facebook/react-native/issues/52854
 *   - `onLayout`, however, still fires reliably — so the accepted community
 *     workaround (and ours) is to render an invisible <Text> that mirrors the
 *     draft, pin it to the input's measured width + line height, and read its
 *     laid-out height via onLayout.
 *
 * web (react-native-web):
 *   - `onContentSizeChange` reports a stale/clamped height immediately after a
 *     large paste; it only catches up on subsequent edits, so growth lags
 *     several keystrokes behind. The same mirror (driven by a ResizeObserver
 *     onLayout, which fires after reflow) fixes this too.
 *
 * Android:
 *   - `onContentSizeChange` works correctly, so Android keeps using it directly
 *     and renders no mirror.
 *
 * If/when #52854 is fixed upstream, the iOS/web mirror (search "measureMirror"
 * and "MIRROR_WRAP_WIDTH_INSET") can be removed and all platforms can share
 * onContentSizeChange again.
 */

const CHAT_MAX_CHARS = 5000
const TEXT_INPUT_FONT_SIZE = 15

// Explicit line height applied to BOTH the real TextInput and the hidden iOS
// measuring mirror, so their wrapped heights match exactly. Without a fixed
// value, iOS renders the TextInput at a taller default line height than a plain
// Text, making the mirror under-report and the expansion lag behind clipping.
// At 20px each line is 20 tall, so the stacked frame steps by 20 per line: the
// 2-line default fits 40 of inner content, and the 8-line max fits 160.
const TEXT_INPUT_LINE_HEIGHT = 20

// Word-level expansion tuning (iOS mirror only).
//
// The real iOS TextInput lays out text inside internal text-container padding
// that the plain measuring <Text> does not have, so for the same box width the
// input wraps to a new line a few words SOONER than the mirror. Because line
// height is matched, this shows up purely as a horizontal (wrap-width)
// difference — the lag is measured in words, not lines, so a vertical/line
// bias cannot fix it.
//
// Fix: pin the mirror's wrap width to the input's measured width minus this
// inset, so the mirror wraps at the same word — or, with a positive inset, a
// little earlier. Larger value → wraps (and expands) earlier / fewer words per
// line; smaller value → later. Clipping late is worse than expanding early, so
// we keep a small positive inset.
const MIRROR_WRAP_WIDTH_INSET = 16

// ── Exported height constants (used by SidekickView for avatar coordination) ──

/** Height of the fixed button row at the bottom of the cluster. */
export const CLUSTER_BTN_ROW_HEIGHT = 56
/** Default (minimum) height of the text input area. */
export const CLUSTER_TEXT_AREA_DEFAULT = 56
/** Approximate height of one text line (used for expansion steps). */
export const CLUSTER_LINE_HEIGHT = Math.ceil(TEXT_INPUT_FONT_SIZE * 1.6)

const MAX_TEXT_LINES = 8

// The stacked text area grows one line at a time rather than doubling. The
// collapsed/default frame already shows MIN_STACKED_LINES (2) lines worth of
// text, and each additional line adds exactly one text line height
// (TEXT_INPUT_LINE_HEIGHT) to the frame — so expansion runs 2 → 3 → 4 … → 8,
// after which the input scrolls. Per line we add TEXT_INPUT_LINE_HEIGHT (the
// height the wrapped text actually occupies), not CLUSTER_LINE_HEIGHT, so the
// frame tracks the text exactly.
const MIN_STACKED_LINES = 2
const TEXT_AREA_MAX =
  CLUSTER_TEXT_AREA_DEFAULT + (MAX_TEXT_LINES - MIN_STACKED_LINES) * TEXT_INPUT_LINE_HEIGHT

// Vertical padding inside textAreaContainer consumed by the container itself.
// onContentSizeChange reports raw text height, so we must subtract this to get
// the inner height available to the TextInput before comparing to step thresholds.
const TEXT_AREA_PADDING_VERTICAL = 10 + 6 // paddingTop + paddingBottom

// Explicit single-line height for the inline web textarea. Without it, the
// browser renders a 2-row textarea, so even an empty input measures taller than
// one line and falsely trips the overflow → stacked transition.
const WEB_SINGLE_LINE_HEIGHT = Math.round(TEXT_INPUT_FONT_SIZE * 1.4)

// Content height above which the inline single-line input is considered to have
// wrapped, triggering expansion into the stacked layout. Sits between one and
// two lines so a single line never trips it on any platform.
const INLINE_OVERFLOW_THRESHOLD = Math.round(CLUSTER_LINE_HEIGHT * 1.4)

const PILL_RADIUS_DOUBLE = 24 // rounded rectangle for double-height pill
const PILL_RADIUS_SINGLE = 9999 // full pill for single-height states
const ICON_BTN_SIZE = 40
const TALK_BTN_SIZE = 44

// Spring config for the pill height / border-radius animation.
// Slightly overdamped so there is no bounce; quick settle (~160 ms).
const PILL_SPRING_CONFIG = { damping: 28, stiffness: 280, mass: 0.8 }

export interface CompositeInputClusterProps {
  onMenuPress?: () => void
  /**
   * NOTE: The external style prop is intentionally not forwarded to the pill
   * container. The pill fills its parent via flexGrow: 1 — forwarding a
   * flex: 1 style (which sets flexBasis: 0) can override the explicit height
   * on the vertical axis in certain Yoga paths.
   */
  style?: ViewStyle
  /**
   * When provided, an absoluteFill overlay is rendered over the entire cluster.
   * Any touch calls this instead of performing the real action. Used in
   * unauthenticated views to gate every interaction behind a login prompt.
   */
  onInteractionBlocked?: () => void
  /**
   * Optional ReactNode rendered between the Menu button and media controls
   * in the button row (e.g. a chat/library mode toggle icon button).
   * Should be a 40×40 hit-area icon button with no background.
   */
  modeToggle?: React.ReactNode
  /**
   * When provided, media-specific controls are added to the left section of
   * the button row (right of modeToggle):
   *   • "playback" → play/pause button
   *   • "navigation" → back/forward buttons
   *   • "none" / undefined → no extra buttons
   */
  controlsConfig?: ControlsConfig
  /**
   * Called whenever the text area height changes due to content growth.
   * Height in logical pixels, starting at CLUSTER_TEXT_AREA_DEFAULT.
   * Used by SidekickView to coordinate avatar size downgrading.
   */
  onTextAreaHeightChange?: (height: number) => void
}

/**
 * CompositeInputCluster — integrated double-height input bar.
 *
 * Default layout (always shown):
 *   ┌─────────────────────────────────────────────────────┐
 *   │  Ask anything (TextInput, grows up to 8 lines)      │  ← text area (56–192 px)
 *   ├─────────────────────────────────────────────────────┤
 *   │  [☰] [toggle] [media…]     [↑ send?]  [● talk]    │  ← button row (56 px fixed)
 *   └─────────────────────────────────────────────────────┘
 *
 * States:
 *   • Thinking  — ThinkingDots: single-line pill on wide views, double-height
 *                 (dots in the top area) on narrow views — matching the height
 *                 of the default input bar for that width.
 *   • Listening — waveform + ×/✓ controls. Single-line inline pill on wide
 *                 views, double-height (waveform top, controls bottom) on narrow
 *                 views — matching the default bar height for that width.
 *   • Loading   — spinner + × control, width-aware like Listening above.
 *   • Processing— double-height: ThinkingDots top (+ "⋯" top-right), empty bottom
 *   • Default   — double-height as above
 */
export const CompositeInputCluster = observer(function CompositeInputCluster({
  onMenuPress,
  onInteractionBlocked,
  modeToggle,
  controlsConfig,
  onTextAreaHeightChange,
}: CompositeInputClusterProps) {
  const { themed, theme } = useAppTheme()

  const {
    inputMode,
    canStartNewInteraction,
    isListening,
    isMicLoading,
    startTalkMode,
    startTypeMode,
    returnToIdle,
  } = useSidekickInteraction()

  const { draftMessage, setDraftMessage, setAwaitingUserSentence, setAwaitingESKResponse } =
    useTyping()

  const {
    sendMessage,
    startMicStream,
    stopMicStream,
    cancelMicStream,
    sendAudioChunk,
    wordsDone,
    isSidekickBusy,
    isSwitchingSidekick,
  } = useChat()

  // Text area height snaps between three discrete steps as the user types.
  const [textAreaHeight, setTextAreaHeight] = useState(CLUSTER_TEXT_AREA_DEFAULT)
  // Scroll is only enabled once content exceeds the 8-line maximum display height.
  const [textScrollEnabled, setTextScrollEnabled] = useState(false)
  // iOS only: the real TextInput's measured width, used to size the hidden
  // mirror Text so it wraps at the same word as the input (see the mirror and
  // MIRROR_WRAP_WIDTH_INSET). Null until the input first reports its layout.
  const [inputMeasuredWidth, setInputMeasuredWidth] = useState<number | null>(null)

  // Once the draft text wraps beyond a single line we commit to the stacked
  // configuration and stay there until the draft clears. This prevents
  // oscillation: the stacked input is full-width, so the same text may fit on
  // one line again, which would otherwise flip us back to inline.
  const [hasExpanded, setHasExpanded] = useState(false)

  // Inline single-row layout: a single line of text (rendered with the same
  // multiline TextInput) sits between the left and right controls. This is the
  // default on all screen sizes; the component only switches to the stacked
  // double-height layout when the text wraps beyond one line.
  const useInlineLayout = !hasExpanded && inputMode !== "talk"
  const totalHeight = useInlineLayout
    ? CLUSTER_BTN_ROW_HEIGHT
    : textAreaHeight + CLUSTER_BTN_ROW_HEIGHT

  // ── Reanimated: native pill height + border-radius ─────────────────────────
  // Animates on the UI thread so the expansion is never blocked by JS work.
  // Border radius is interpolated alongside height so the pill shape morphs
  // smoothly from full-pill (collapsed, radius = height/2) to rounded-rect
  // (expanded, PILL_RADIUS_DOUBLE). Interpolating from PILL_RADIUS_SINGLE
  // (9999) would produce large intermediate values, so we cap the start at the
  // effective full-pill radius for the collapsed height (CLUSTER_BTN_ROW_HEIGHT/2).
  const animatedHeight = useSharedValue(totalHeight)
  const animatedPillStyle = useAnimatedStyle(() => ({
    height: animatedHeight.value,
    borderRadius: interpolate(
      animatedHeight.value,
      [CLUSTER_BTN_ROW_HEIGHT, CLUSTER_BTN_ROW_HEIGHT + CLUSTER_TEXT_AREA_DEFAULT],
      [CLUSTER_BTN_ROW_HEIGHT / 2, PILL_RADIUS_DOUBLE],
      "clamp",
    ),
  }))
  useEffect(() => {
    animatedHeight.value = withSpring(totalHeight, PILL_SPRING_CONFIG)
  }, [totalHeight, animatedHeight])

  // Report text area height changes to parent for avatar coordination.
  const prevTextAreaHeightRef = useRef(textAreaHeight)
  useEffect(() => {
    if (textAreaHeight !== prevTextAreaHeightRef.current) {
      prevTextAreaHeightRef.current = textAreaHeight
      onTextAreaHeightChange?.(textAreaHeight)
    }
  }, [textAreaHeight, onTextAreaHeightChange])

  // Always-current mirror of inputMode for use in async callbacks.
  const inputModeRef = useRef(inputMode)
  useEffect(() => {
    inputModeRef.current = inputMode
  }, [inputMode])

  // The TextInput is always at the same position in the tree — it never
  // remounts across the collapsed/expanded transition on any platform — so no
  // focus restoration is needed here.
  const inputRef = useRef<TextInput>(null)

  // ── Mic stream ────────────────────────────────────────────────────────────

  const handleMicError = useCallback(() => {
    cancelMicStream()
    returnToIdle()
  }, [cancelMicStream, returnToIdle])

  const { volume, startStream, stopStream } = useMicrophoneStream(
    startMicStream,
    stopMicStream,
    sendAudioChunk,
    handleMicError,
  )

  // Signal the typing context when listening starts.
  useEffect(() => {
    if (isListening) {
      setAwaitingESKResponse(true)
      setAwaitingUserSentence(true)
    }
  }, [isListening, setAwaitingESKResponse, setAwaitingUserSentence])

  // Stop mic when inputMode is forced to idle externally (e.g. video play).
  useEffect(() => {
    if (inputMode === "idle" && isListening) {
      stopStream({ immediate: true })
    }
  }, [inputMode, isListening, stopStream])

  // Stop mic when app goes to background.
  useEffect(() => {
    const sub = AppState.addEventListener("change", (nextState: AppStateStatus) => {
      if (
        (nextState === "background" || nextState === "inactive") &&
        (isListening || isMicLoading)
      ) {
        cancelMicStream()
        stopStream({ immediate: true })
        returnToIdle()
      }
    })
    return () => sub.remove()
  }, [isListening, isMicLoading, cancelMicStream, stopStream, returnToIdle])

  // Return to idle when keyboard is dismissed (hardware back, swipe-down, etc.).
  useEffect(() => {
    if (Platform.OS === "web") return
    const sub = KeyboardEvents.addListener("keyboardWillHide", () => {
      if (inputModeRef.current === "type") returnToIdle()
    })
    return () => sub.remove()
  }, [returnToIdle])

  // Collapse text area back to default height only when there is no draft text,
  // or when entering talk mode (which shows different UI in the top section).
  // Dismissing the keyboard alone should NOT collapse — the user may re-focus
  // and continue editing their existing draft.
  useEffect(() => {
    const shouldCollapse = inputMode === "talk" || !draftMessage
    if (shouldCollapse) {
      if (textAreaHeight > CLUSTER_TEXT_AREA_DEFAULT) {
        setTextAreaHeight(CLUSTER_TEXT_AREA_DEFAULT)
      }
      // Allow the inline layout to be used again for the next draft.
      if (hasExpanded) setHasExpanded(false)
    }
  }, [inputMode, draftMessage, textAreaHeight, hasExpanded])

  // ── Talk-mode actions ─────────────────────────────────────────────────────

  const handleTalkPress = useCallback(() => {
    if (!canStartNewInteraction) return
    Keyboard.dismiss()
    setDraftMessage("")
    startTalkMode()
    startStream()
  }, [canStartNewInteraction, startTalkMode, startStream, setDraftMessage])

  const handleCancelTalk = useCallback(() => {
    cancelMicStream()
    stopStream({ immediate: true })
    returnToIdle()
  }, [cancelMicStream, stopStream, returnToIdle])

  const handleAcceptTalk = useCallback(() => {
    stopStream()
  }, [stopStream])

  // ── Text-input actions ────────────────────────────────────────────────────

  const handleTextFocus = useCallback(() => {
    if (inputMode !== "type") startTypeMode()
  }, [inputMode, startTypeMode])

  const handleChangeText = useCallback(
    (text: string) => {
      if (text.length > CHAT_MAX_CHARS) {
        setDraftMessage(text.slice(0, CHAT_MAX_CHARS))
        return
      }
      setDraftMessage(text)
      if (inputMode !== "type") startTypeMode()
    },
    [setDraftMessage, inputMode, startTypeMode],
  )

  // Drive text area height from the TextInput's reported content size.
  // Once stacked, the frame grows one text line at a time: the number of lines
  // the content needs (contentH / TEXT_INPUT_LINE_HEIGHT) is clamped to
  // [MIN_STACKED_LINES, MAX_TEXT_LINES] and the frame height follows linearly.
  // Past MAX_TEXT_LINES the input scrolls.
  //
  //   2 lines → default, 3 → +1 line, 4 → +2 lines … up to MAX_TEXT_LINES
  //   beyond MAX_TEXT_LINES → max height + scrollEnabled
  // Core stepping logic, given the raw text content height (no container
  // padding). Shared by two height sources:
  //   • Android / web → TextInput.onContentSizeChange (reliable there)
  //   • iOS           → a hidden mirror <Text> measured via onLayout, because
  //     iOS New Architecture (Fabric) never fires onContentSizeChange for a
  //     multiline TextInput. onLayout, however, fires reliably.
  const applyContentHeight = useCallback(
    (contentH: number) => {
      // The inner (padding-subtracted) height available at the maximum step.
      // Content height excludes the container's vertical padding, so we
      // subtract it before comparing. Once content exceeds this the input
      // scrolls. Early expansion is handled horizontally on iOS via
      // MIRROR_WRAP_WIDTH_INSET (the mirror wraps at/just before the input's
      // word), not by biasing these heights.
      const innerMax = TEXT_AREA_MAX - TEXT_AREA_PADDING_VERTICAL

      if (contentH <= 0) return

      // Inline layout: stay on a single line until the text wraps. The same
      // height signal that drives the stacked steps tells us when the single
      // line has overflowed; at that point we commit to the stacked
      // configuration (hasExpanded) and fall through to the step logic below.
      //
      // An empty draft can never overflow — guarding on it prevents an infinite
      // loop: empty inputs that briefly mis-measure would otherwise toggle
      // hasExpanded on, the collapse effect would toggle it back off (no draft),
      // and the layout would oscillate every commit.
      if (!hasExpanded) {
        const wrapped = !!draftMessage && contentH > INLINE_OVERFLOW_THRESHOLD
        if (!wrapped) {
          setTextScrollEnabled(false)
          return
        }
        // Expand to the stacked layout. textAreaHeight is already
        // CLUSTER_TEXT_AREA_DEFAULT (it never changes while collapsed), so the
        // Reanimated spring takes us directly to the correct 2× height.
        //
        // We do NOT fall through to the step logic here: the measurement was
        // taken in the narrow collapsed container (text squeezed between
        // buttons) and would pick an oversized step. The next
        // onContentSizeChange fires after the layout settles at the full
        // stacked width and will step up from there only if truly needed.
        setHasExpanded(true)
        return
      }

      // Stacked layout: grow one text line at a time. Determine how many lines
      // the content needs (each line is TEXT_INPUT_LINE_HEIGHT tall), clamp to
      // [MIN_STACKED_LINES, MAX_TEXT_LINES], and size the frame linearly from
      // the default (MIN_STACKED_LINES) height.
      const neededLines = Math.min(
        MAX_TEXT_LINES,
        Math.max(MIN_STACKED_LINES, Math.ceil(contentH / TEXT_INPUT_LINE_HEIGHT)),
      )
      const newH =
        CLUSTER_TEXT_AREA_DEFAULT + (neededLines - MIN_STACKED_LINES) * TEXT_INPUT_LINE_HEIGHT

      const shouldScroll = contentH > innerMax
      setTextScrollEnabled(shouldScroll)

      if (newH !== textAreaHeight) {
        setTextAreaHeight(newH)
      }
    },
    [textAreaHeight, hasExpanded, draftMessage],
  )

  // Android height source for both layouts (inline wrap-detection and stacked
  // stepping). iOS and web are driven by the hidden mirror Text instead:
  //   • iOS never fires onContentSizeChange (Fabric):
  //     https://github.com/facebook/react-native/issues/52854
  //   • web (react-native-web) reports a stale/clamped height immediately after
  //     a large paste, so growth would lag several keystrokes behind.
  // The mirror's onLayout fires reliably (after reflow) on both.
  const handleContentSizeChange = useCallback(
    (e: { nativeEvent: { contentSize: { height: number } } }) => {
      if (Platform.OS !== "android") return
      applyContentHeight(e.nativeEvent.contentSize.height)
    },
    [applyContentHeight],
  )

  // iOS height source: the hidden mirror Text reports its laid-out height.
  const handleMeasuredTextLayout = useCallback(
    (e: { nativeEvent: { layout: { height: number } } }) => {
      applyContentHeight(e.nativeEvent.layout.height)
    },
    [applyContentHeight],
  )

  const isDisabled = isSwitchingSidekick || !wordsDone || isSidekickBusy
  const hasDraft = !!(draftMessage && draftMessage.trim().length > 0)

  const handleSend = useCallback(() => {
    if (!hasDraft || isDisabled) return
    sendMessage(draftMessage)
    setDraftMessage("")
    Keyboard.dismiss()
    returnToIdle()
    setTextAreaHeight(CLUSTER_TEXT_AREA_DEFAULT)
    setHasExpanded(false)
  }, [hasDraft, isDisabled, sendMessage, draftMessage, setDraftMessage, returnToIdle])

  const handleMenuPress = useCallback(() => {
    Keyboard.dismiss()
    onMenuPress?.()
  }, [onMenuPress])

  // Web: Enter to send, Shift+Enter for newline.
  const handleKeyPress = useCallback(
    (e: any) => {
      if (e.nativeEvent.key === "Enter" && !e.nativeEvent.shiftKey) {
        e.preventDefault()
        handleSend()
      }
    },
    [handleSend],
  )

  const isInTalkMode = inputMode === "talk"
  const isProcessingAudio = isInTalkMode && !isListening && !isMicLoading

  // ── THINKING / WAITING / PROCESSING STATE ────────────────────────────────
  // Shown when the sidekick is processing a response. Includes the
  // isProcessingAudio case (user accepted a recording — mic stopped, backend
  // is processing) so the height is consistent whether the user pressed accept
  // or cancel. In blocked mode the cluster always renders the normal input bar
  // so the overlay has a visible target.
  //
  // The pill height matches the single-line default so there is no jump when
  // entering or leaving this state.

  if (!canStartNewInteraction && (!isInTalkMode || isProcessingAudio) && !onInteractionBlocked) {
    return (
      <View style={[styles.singlePill, themed($pillTheme)]}>
        <View style={styles.centeredContent}>
          <ThinkingDots color={theme.colors.buttonText} dotSize={5} dotSpacing={2} />
        </View>
      </View>
    )
  }

  // ── Inline media controls ─────────────────────────────────────────────────

  const mediaControls = (() => {
    if (!controlsConfig || controlsConfig.type === "none") return null

    if (controlsConfig.type === "playback") {
      // Play/pause is now rendered in the scrubber row (left of the track)
      // via PlaybackScrubber — nothing extra in the cluster button row.
      return null
    }

    if (controlsConfig.type === "navigation") {
      const cfg = controlsConfig as NavigationControlsConfig
      return (
        <>
          <TouchableOpacity
            style={styles.iconBtn}
            onPress={cfg.onGoBack}
            disabled={!cfg.canGoBack}
            accessibilityLabel="Go back"
          >
            <IonIcons
              name="caret-back"
              size={20}
              color={cfg.canGoBack ? theme.colors.buttonText : theme.colors.textDim}
            />
          </TouchableOpacity>
          <TouchableOpacity
            style={styles.iconBtn}
            onPress={cfg.onGoForward}
            disabled={!cfg.canGoForward}
            accessibilityLabel="Go forward"
          >
            <IonIcons
              name="caret-forward"
              size={20}
              color={cfg.canGoForward ? theme.colors.buttonText : theme.colors.textDim}
            />
          </TouchableOpacity>
        </>
      )
    }

    return null
  })()

  // ── INLINE TALK PILL (all screen sizes, listening / loading) ─────────────
  // The default bar is always a single line, so talk mode uses a single-line
  // pill on all screen sizes: waveform / spinner fills the centre and the
  // cancel / accept controls sit on the right.

  if (isInTalkMode) {
    return (
      <View style={[styles.singlePill, themed($pillTheme), { height: CLUSTER_BTN_ROW_HEIGHT }]}>
        <View style={styles.talkFeedback}>
          {isMicLoading ? (
            <ActivityIndicator color={theme.colors.buttonText} size="small" />
          ) : isListening ? (
            <WaveformAnimation
              volume={volume}
              barCount={9}
              barWidth={4}
              height={26}
              color={theme.colors.buttonText}
            />
          ) : (
            <ThinkingDots color={theme.colors.buttonText} dotSize={5} dotSpacing={2} />
          )}
        </View>

        {!isProcessingAudio && (
          <TouchableOpacity
            style={styles.iconBtn}
            onPress={handleCancelTalk}
            accessibilityLabel="Cancel recording"
          >
            <IonIcons name="close" size={22} color={theme.colors.buttonText} />
          </TouchableOpacity>
        )}
        {isListening && (
          <TouchableOpacity
            style={[
              styles.talkBtn,
              styles.sendBtn,
              { marginLeft: 6, borderColor: theme.colors.buttonText },
            ]}
            onPress={handleAcceptTalk}
            accessibilityLabel="Send recording"
          >
            <IonIcons name="arrow-up" size={20} color={theme.colors.buttonText} />
          </TouchableOpacity>
        )}

        {onInteractionBlocked && (
          <TouchableOpacity
            style={StyleSheet.absoluteFill}
            onPress={onInteractionBlocked}
            activeOpacity={1}
            accessibilityLabel="Sign in to interact"
          />
        )}
      </View>
    )
  }

  // ── UNIFIED LAYOUT (all platforms: single-height or expanded) ───────────────
  //
  // This is the only layout — it covers both the single-height (collapsed) and
  // expanded states on every platform. The TextInput is always at tree-index [1]
  // inside the text row, so it never unmounts across the transition. This
  // prevents the keyboard-dismiss-and-reopen that a subtree switch would cause.
  //
  // When collapsed (!hasExpanded):
  //   • text row acts like an inline row (56 px): left controls + text + send/talk
  //   • button row is not rendered (controls live in the text row)
  //   • pill border-radius = full pill (PILL_RADIUS_SINGLE)
  //
  // When expanded (hasExpanded):
  //   • text row is the stacked text area (textAreaHeight)
  //   • left/right controls in the text row are hidden via display:"none"
  //   • button row is rendered below the text area
  //   • pill border-radius = rounded rect (PILL_RADIUS_DOUBLE)

  return (
    <Animated.View
      style={[
        styles.doublePill,
        themed($pillTheme),
        // Height + borderRadius animated by Reanimated on all platforms.
        animatedPillStyle,
      ]}
    >
      {/* ── Text row / text area ──────────────────────────────────────────── */}
      {/* Collapsed: collapsedRow (inline-style, 56 px)                        */}
      {/* Expanded:  textAreaContainer (flex: 1, stacked padding)              */}
      <View style={!hasExpanded ? styles.collapsedRow : styles.textAreaContainer}>
        {/* Left controls. Visible when collapsed, hidden when expanded.
            Kept in the tree (display:"none") so the TextInput at index [1] never
            shifts position and is never remounted. */}
        <View style={hasExpanded ? styles.displayNone : styles.inlineLeftButtons}>
          <TouchableOpacity
            style={styles.iconBtn}
            onPress={handleMenuPress}
            accessibilityLabel="Menu"
          >
            <MaterialIcons name="menu" size={22} color={theme.colors.buttonText} />
          </TouchableOpacity>
          {modeToggle}
          {mediaControls}
        </View>

        {/* TextInput — always at tree-index [1], never remounts. */}
        <TextInput
          ref={inputRef}
          style={[
            styles.textInput,
            themed($textInputTheme),
            // Expanded: explicit height so the frame matches the current step.
            // (Not needed on web — flex handles it.)
            Platform.OS !== "web" &&
              hasExpanded && {
                height: textAreaHeight - TEXT_AREA_PADDING_VERTICAL,
              },
            // Collapsed: inline-style centering.
            !hasExpanded && styles.textInputInline,
            !hasExpanded &&
              Platform.OS === "android" &&
              ({
                height: "100%",
                textAlignVertical: "center",
                includeFontPadding: false,
                paddingTop: 0,
              } as any),
            !hasExpanded && Platform.OS === "ios" && { height: CLUSTER_LINE_HEIGHT },
            !hasExpanded &&
              Platform.OS === "web" &&
              ({
                outlineStyle: "none",
                resize: "none",
                overflowY: "hidden",
                height: WEB_SINGLE_LINE_HEIGHT,
                paddingTop: 0,
              } as any),
            hasExpanded &&
              Platform.OS === "web" &&
              ({ outlineStyle: "none", resize: "none" } as any),
          ]}
          value={draftMessage}
          onChangeText={handleChangeText}
          onFocus={handleTextFocus}
          placeholder="Ask anything"
          placeholderTextColor={theme.colors.textDim}
          multiline
          // Collapsed: disable scroll so a single-line input doesn't bounce.
          // Expanded: keep scroll enabled as a safety net for large pastes.
          scrollEnabled={!hasExpanded ? Platform.OS === "android" : true}
          onKeyPress={Platform.OS === "web" ? handleKeyPress : undefined}
          onContentSizeChange={handleContentSizeChange}
          // iOS/web: measure input width so the mirror wraps at the same word.
          onLayout={
            Platform.OS !== "android"
              ? (e) => setInputMeasuredWidth(e.nativeEvent.layout.width)
              : undefined
          }
          blurOnSubmit={false}
          {...({ showsVerticalScrollIndicator: hasExpanded ? textScrollEnabled : false } as object)}
        />

        {/* iOS + web: hidden mirror used to measure text height.
            iOS (Fabric) never fires TextInput.onContentSizeChange (see
            https://github.com/facebook/react-native/issues/52854), and web
            (react-native-web) reports a stale height right after a large paste.
            The mirror's onLayout fires reliably after reflow on both. */}
        {Platform.OS !== "android" && (
          <Text
            style={[
              styles.measureMirror,
              inputMeasuredWidth != null
                ? { width: Math.max(1, inputMeasuredWidth - MIRROR_WRAP_WIDTH_INSET) }
                : { right: 0 },
            ]}
            onLayout={handleMeasuredTextLayout}
            accessibilityElementsHidden
            importantForAccessibility="no-hide-descendants"
            allowFontScaling={false}
          >
            {draftMessage && draftMessage.length > 0 ? draftMessage : " "}
          </Text>
        )}

        {/* Right control. Visible when collapsed, hidden when expanded. */}
        <View style={hasExpanded ? styles.displayNone : undefined}>
          {hasDraft ? (
            <TouchableOpacity
              style={[styles.talkBtn, styles.sendBtn, { borderColor: theme.colors.buttonText }]}
              onPress={handleSend}
              disabled={isDisabled}
              accessibilityLabel="Send message"
            >
              <IonIcons name="arrow-up" size={20} color={theme.colors.buttonText} />
            </TouchableOpacity>
          ) : (
            <TouchableOpacity
              style={[styles.talkBtn, themed($talkBtnTheme)]}
              onPress={handleTalkPress}
              disabled={!canStartNewInteraction}
              accessibilityLabel="Talk"
            >
              <TalkIcon size={20} color={theme.colors.buttonBackground} />
            </TouchableOpacity>
          )}
        </View>
      </View>

      {/* ── Button row ────────────────────────────────────────────────────── */}
      {/* Rendered only when expanded; controls are inline when collapsed. */}
      {hasExpanded && (
        <View style={styles.btnRow}>
          <TouchableOpacity
            style={styles.iconBtn}
            onPress={handleMenuPress}
            accessibilityLabel="Menu"
          >
            <MaterialIcons name="menu" size={22} color={theme.colors.buttonText} />
          </TouchableOpacity>

          {modeToggle}
          {mediaControls}

          <View style={styles.rowSpacer} />

          {hasDraft ? (
            <TouchableOpacity
              style={[styles.talkBtn, styles.sendBtn, { borderColor: theme.colors.buttonText }]}
              onPress={handleSend}
              disabled={isDisabled}
              accessibilityLabel="Send message"
            >
              <IonIcons name="arrow-up" size={20} color={theme.colors.buttonText} />
            </TouchableOpacity>
          ) : (
            <TouchableOpacity
              style={[styles.talkBtn, themed($talkBtnTheme)]}
              onPress={handleTalkPress}
              disabled={!canStartNewInteraction}
              accessibilityLabel="Talk"
            >
              <TalkIcon size={20} color={theme.colors.buttonBackground} />
            </TouchableOpacity>
          )}
        </View>
      )}

      {/* Interaction gate — covers the cluster in unauthenticated views. */}
      {onInteractionBlocked && (
        <TouchableOpacity
          style={StyleSheet.absoluteFill}
          onPress={onInteractionBlocked}
          activeOpacity={1}
          accessibilityLabel="Sign in to interact"
        />
      )}
    </Animated.View>
  )
})

// ── Themed styles ─────────────────────────────────────────────────────────────

const $pillTheme: ThemedStyle<ViewStyle> = ({ colors }) => ({
  backgroundColor: colors.buttonBackground,
})

const $textInputTheme: ThemedStyle<TextStyle> = ({ colors }) => ({
  color: colors.buttonText,
})

// Talk button inverts the pill's own color pair so it always contrasts with
// the pill surface: dark circle on a light pill (light mode) and white circle
// on a dark pill (dark mode).
const $talkBtnTheme: ThemedStyle<ViewStyle> = ({ colors }) => ({
  backgroundColor: colors.buttonText,
})

// ── Static styles ─────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  // Single-height pill for the thinking/waiting state.
  singlePill: {
    flexDirection: "row",
    alignItems: "center",
    borderRadius: PILL_RADIUS_SINGLE,
    height: CLUSTER_BTN_ROW_HEIGHT,
    paddingHorizontal: 8,
    gap: 2,
    overflow: "hidden",
    flexGrow: 1,
  },

  // Centred content area (flex: 1) used in single-height pill.
  centeredContent: {
    flex: 1,
    alignItems: "center",
    justifyContent: "center",
  },

  // Double-height pill container. flexGrow: 1 fills the parent row horizontally.
  // We do NOT use flex: 1 here because flexBasis: 0 can override the explicit
  // height on the vertical axis in some Yoga paths.
  doublePill: {
    flexDirection: "column",
    borderRadius: PILL_RADIUS_DOUBLE,
    overflow: "hidden",
    flexGrow: 1,
  },

  // Top section: text input or talk feedback.
  // flex: 1 fills the pill's remaining height after the button row, so only
  // the outer pill height needs to change — the text area follows automatically.
  // This ensures only the outer container height needs to change — the text area follows automatically.
  textAreaContainer: {
    flex: 1,
    flexDirection: "row",
    alignItems: "stretch",
    paddingHorizontal: 14,
    paddingTop: 10,
    paddingBottom: 6,
  },

  textInput: {
    flex: 1,
    fontSize: TEXT_INPUT_FONT_SIZE,
    lineHeight: TEXT_INPUT_LINE_HEIGHT,
    paddingHorizontal: 0,
    paddingVertical: 0,
    textAlignVertical: "top",
  },

  // iOS-only invisible mirror used to measure the wrapped text height. Its wrap
  // width is set inline to the input's measured width (minus MIRROR_WRAP_WIDTH_INSET)
  // so it wraps at the same word; before that width is known it falls back to
  // right: 0. It shares the input's font size + line height so its measured
  // height matches. It must not constrain its own height (so it grows with
  // content) and must not capture touches.
  measureMirror: {
    position: "absolute",
    top: 0,
    left: 0,
    fontSize: TEXT_INPUT_FONT_SIZE,
    lineHeight: TEXT_INPUT_LINE_HEIGHT,
    opacity: 0,
    pointerEvents: "none",
  },

  // Inline single-line variant: a little horizontal breathing room from the
  // adjacent icon buttons. No vertical padding — iOS multiline inputs have
  // symmetric internal top/bottom insets, so a content-sized single line is
  // centred naturally by the row's alignItems. Android and web apply their own
  // centring in the TextInput style array.
  textInputInline: {
    paddingHorizontal: 6,
    paddingVertical: 0,
    textAlignVertical: "center",
  },

  // Collapsed state: the text row doubles as the full pill content —
  // left controls, TextInput, and right control all in one row.
  // Uses flex: 1 so it fills the outer container (CLUSTER_BTN_ROW_HEIGHT tall).
  collapsedRow: {
    flex: 1,
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 8,
    gap: 2,
  },

  // Wrapper for the left control group in the collapsed row.
  inlineLeftButtons: {
    flexDirection: "row",
    alignItems: "center",
    gap: 2,
  },

  // Hides a view without unmounting it. display:"none" removes the node from
  // the flex layout (takes no space) but keeps it in the render tree so
  // sibling indices — and thus the TextInput's tree position — are stable.
  displayNone: {
    display: "none",
  },

  // Talk mode: waveform / spinner / dots fill remaining flex space.
  talkFeedback: {
    flex: 1,
    alignItems: "center",
    justifyContent: "center",
  },

  // Fixed-height button row at the bottom of the double-height pill.
  btnRow: {
    height: CLUSTER_BTN_ROW_HEIGHT,
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 8,
    gap: 2,
  },

  // Pushes right-side buttons (send, talk) to the right.
  rowSpacer: {
    flex: 1,
  },

  // Standard 40×40 hit-area for icon-only buttons inside the pill.
  iconBtn: {
    width: ICON_BTN_SIZE,
    height: ICON_BTN_SIZE,
    justifyContent: "center",
    alignItems: "center",
  },

  // Talk button: filled circle that inverts the pill's surface color.
  // Background color is supplied by $talkBtnTheme (themed style).
  talkBtn: {
    width: TALK_BTN_SIZE,
    height: TALK_BTN_SIZE,
    borderRadius: 9999,
    justifyContent: "center",
    alignItems: "center",
    marginRight: 2,
  },

  // Send button: outlined circle with transparent fill and a 2.5 px stroke.
  // Border color is applied inline so it can reference the theme token.
  sendBtn: {
    backgroundColor: "transparent",
    borderWidth: 2.5,
  },
})
